import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useMemo, cloneElement } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { IMAGE_SIZE_VARIANTS, DEFAULT_IMAGE_SIZE_VARIANT } from '../../constants.js';
import { useGetResponsiveImageSizes, useGetRelativeImageSizeInLayout } from '../../hooks/responsiveImageSizeHooks.js';
import { getResponsiveSx, getStartingSize } from '../../utils/styleHelpers.js';
import { getBodyTextStyles } from '../../utils/textStyleHelpers.js';
import { b as getImageSizeMapping, i as isImageResizerProps } from '../../utils/imageHelpers2.js';
import { jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { B as Box } from '../Box/Box.js';
import { F as FramedImage } from '../FramedImage/FramedImage.js';
import { useTheme } from '../../hooks/useTheme.js';
import { useGetCurrentSizeClass } from '../../hooks/useGetCurrentSizeClass.js';
import { hFlex } from '../../utils/sxChunks.js';

function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function getContainerSx(_ref) {
  var size = _ref.size,
    base = _ref.theme.base;
  var sizeMapping = getImageSizeMapping("Avatar", {
    base: base
  });
  return {
    width: sizeMapping[size],
    height: sizeMapping[size]
  };
}
var getResponsiveContainerSx = function getResponsiveContainerSx(props) {
  return getResponsiveSx(_objectSpread$1(_objectSpread$1({}, props), {}, {
    renderSxAtSize: getContainerSx
  }));
};
function getTextSx(_ref2) {
  var size = _ref2.size,
    theme = _ref2.theme;
  switch (size) {
    case "small":
      return getBodyTextStyles({
        size: "xxSmall",
        weight: "bold",
        themeProps: theme
      });
    case "large":
      return getBodyTextStyles({
        size: "medium",
        weight: "bold",
        themeProps: theme
      });
    case "xLarge":
      return getBodyTextStyles({
        size: "medium",
        weight: "bold",
        themeProps: theme
      });
    case "medium":
    default:
      return getBodyTextStyles({
        size: "xSmall",
        weight: "bold",
        themeProps: theme
      });
  }
}
var getResponsiveTextSx = function getResponsiveTextSx(props) {
  return getResponsiveSx(_objectSpread$1(_objectSpread$1({}, props), {}, {
    renderSxAtSize: getTextSx
  }));
};

var _excluded = ["initials", "size", "loading", "testId", "className", "sx", "use"],
  _excluded2 = ["imageUrl", "defaultImageUrl", "defaultImageClassName", "imageResizeServiceUrl"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var AVATAR_DEFAULT_IMAGE = "https://biome.immutable.com/hosted-assets/images/avatarDefaultImage.svg";
function Avatar(_ref) {
  var initials = _ref.initials,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_IMAGE_SIZE_VARIANT : _ref$size,
    loading = _ref.loading,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "Avatar" : _ref$testId,
    className = _ref.className,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    use = _ref.use,
    props = _objectWithoutProperties(_ref, _excluded);
  var _ref2 = isImageResizerProps(use) ? props : _objectSpread(_objectSpread({}, props), {}, {
      imageUrl: undefined,
      defaultImageUrl: undefined,
      imageResizeServiceUrl: undefined,
      defaultImageClassName: undefined
    }),
    imageUrl = _ref2.imageUrl,
    _ref2$defaultImageUrl = _ref2.defaultImageUrl,
    defaultImageUrl = _ref2$defaultImageUrl === void 0 ? AVATAR_DEFAULT_IMAGE : _ref2$defaultImageUrl,
    defaultImageClassName = _ref2.defaultImageClassName,
    imageResizeServiceUrl = _ref2.imageResizeServiceUrl,
    otherProps = _objectWithoutProperties(_ref2, _excluded2);
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_IMAGE_SIZE_VARIANT, IMAGE_SIZE_VARIANTS);
  var currentSizeClass = useGetCurrentSizeClass(size, DEFAULT_IMAGE_SIZE_VARIANT, IMAGE_SIZE_VARIANTS);
  var mergedContainerSx = useMemo(function () {
    var _theme$components$Ava, _theme$components;
    return merge(_objectSpread(_objectSpread({}, hFlex), {}, {
      borderRadius: "50%",
      overflow: "hidden",
      alignItems: "center",
      justifyContent: "center",
      pos: "relative"
    }, initials ? {
      bg: "base.color.accent.3"
    } : {}), getContainerSx({
      theme: theme,
      size: startingSize
    }), getResponsiveContainerSx({
      theme: theme,
      size: size
    }), (_theme$components$Ava = (_theme$components = theme.components) === null || _theme$components === void 0 || (_theme$components = _theme$components.Avatar) === null || _theme$components === void 0 ? void 0 : _theme$components.sxOverride) !== null && _theme$components$Ava !== void 0 ? _theme$components$Ava : {}, sx);
  }, [initials, size, startingSize, sx, theme]);
  var textSx = merge({
    c: "base.color.fixed.black.1000"
  }, getTextSx({
    theme: theme,
    size: startingSize
  }), getResponsiveTextSx({
    theme: theme,
    size: size
  }));
  var responsiveSizes = useGetResponsiveImageSizes(size, "Avatar");
  var relativeImageSizeInLayout = useGetRelativeImageSizeInLayout(size, "Avatar");
  var relativeImageSizeInLayoutNumber = Number.parseInt(relativeImageSizeInLayout, 10);
  var framedImageProps = useMemo(function () {
    return use ? {
      use: /*#__PURE__*/cloneElement(use, {
        width: relativeImageSizeInLayoutNumber,
        height: relativeImageSizeInLayoutNumber
      }),
      loading: loading
    } : {
      imageUrl: imageUrl,
      responsiveSizes: responsiveSizes,
      relativeImageSizeInLayout: relativeImageSizeInLayout,
      imageResizeServiceUrl: imageResizeServiceUrl,
      defaultImageClassName: defaultImageClassName,
      defaultImageUrl: defaultImageUrl,
      loading: loading
    };
  }, [imageUrl, responsiveSizes, relativeImageSizeInLayout, relativeImageSizeInLayoutNumber, imageResizeServiceUrl, defaultImageClassName, defaultImageUrl, loading, use]);
  return jsx(Box, _objectSpread(_objectSpread({}, otherProps), {}, {
    testId: testId,
    className: "".concat(className !== null && className !== void 0 ? className : "", " Avatar Avatar--").concat(currentSizeClass),
    sx: mergedContainerSx,
    children: imageUrl || !initials && !imageUrl ? jsx(FramedImage, _objectSpread(_objectSpread({}, framedImageProps), {}, {
      circularFrame: true,
      testId: "".concat(testId, "__framedImage"),
      className: "Avatar__image"
    })) : initials ? jsx(Box, {
      sx: textSx,
      testId: "".concat(testId, "__text"),
      rc: jsx("span", {}),
      className: "Avatar__initials",
      children: initials
    }) : null
  }));
}
Avatar.displayName = "Avatar";

export { Avatar as A };
//# sourceMappingURL=Avatar.js.map
